import type { CustomerNotificationParams } from '@kizen/api/customer-notifications';
import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { RequestOptions, StringifiedParams } from './types';
import { curry1 } from './utils';

type SummaryRequest =
  paths['/api/bulk-actions/summary']['post']['requestBody']['content']['application/json'];

interface BulkActionNotificationRequest
  extends Pick<SummaryRequest, 'record_ids' | 'search_text'> {
  action: Extract<SummaryRequest['action'], `notifications_${string}`>;
  filters?: StringifiedParams<
    Pick<
      CustomerNotificationParams,
      | 'archived'
      | 'date_from'
      | 'date_to'
      | 'notification_type'
      | 'read'
      | 'scope'
    >
  >;
}

interface BulkActionRequest extends Omit<SummaryRequest, 'action'> {
  action: Exclude<
    SummaryRequest['action'],
    BulkActionNotificationRequest['action']
  >;
}

export type BulkActionSummary = Mutable<
  paths['/api/bulk-actions/summary/{summary_id}']['get']['responses']['200']['content']['application/json']
>;

export type BulkActionSummaryInitializeRequest =
  | BulkActionRequest
  | BulkActionNotificationRequest;

export type BulkActionSummaryInitializeResponse =
  paths['/api/bulk-actions/summary']['post']['responses']['200']['content']['application/json'];

export const bulkActionSummaryInitialize = async (
  instance: AxiosInstance,
  data: BulkActionSummaryInitializeRequest,
  options: RequestOptions = {}
): Promise<BulkActionSummaryInitializeResponse> => {
  const res = await instance.post<BulkActionSummaryInitializeResponse>(
    '/bulk-actions/summary',
    data,
    options
  );

  return res.data;
};

export const bulkActionSummary = async (
  instance: AxiosInstance,
  summaryId: string,
  options: RequestOptions = {}
): Promise<BulkActionSummary> => {
  const res = await instance.get<BulkActionSummary>(
    `/bulk-actions/summary/${summaryId}`,
    options
  );

  return res.data;
};

export class BulkActionService {
  public bulkActionSummary;
  public bulkActionSummaryInitialize;

  constructor(private instance: AxiosInstance) {
    this.bulkActionSummary = curry1(bulkActionSummary)(this.instance);
    this.bulkActionSummaryInitialize = curry1(bulkActionSummaryInitialize)(
      this.instance
    );
  }
}
